/** @file ViewportStatus: the status line (joint, parent, picks, rotation, frame, tool hints). */
import type { ReactElement } from 'react';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { PoseOverride, Tool } from '../../state/editorTypes';
import { useEditor } from '../../state/useEditor';
import { startKeyOf } from './startKeyOf';
import type { HudPick, ViewportHud } from './viewportHud';
import { VIEWPORT_STYLES as S } from './viewportStyles';

/** One hint line per tool (the mouse rules every tool shares come after it). */
const HINTS: Record<Tool, string> = {
  hand: 'drag orbits, Shift+drag tilts', move: 'drag an arrow or a plane square (centre: view plane)', rotate: 'drag a ring (outer: free), Ctrl snaps',
  scale: 'drag the centre cube (one uniform scale)', ik: 'drag a joint with an IK chain to place it',
};

const fmt = (v: number): string => (Math.round(v * 10) / 10).toFixed(1);

type Selected = NonNullable<ViewportHud['selected']>;

/** `name < parent`, or a note when nothing is selected. */
const jointText = (sel: Selected | null): string => (sel === null ? 'no joint selected' : sel.parent === null ? sel.name : `${sel.name} < ${sel.parent}`);

/** The first pick of the joint's drawables: entry, tile, flip, row, empty slot. */
function pickText(pick: HudPick | undefined): string | null {
  if (pick === undefined) return null;
  return `entry ${String(pick.entry)} tile ${String(pick.tile)}${pick.flip ? ' flip' : ''} row ${String(pick.row)}${pick.missing ? ' (empty slot)' : ''}`;
}

/** The joint's key rotation: the dragged one while a gesture runs, else the document's at the frame. */
function rotText(sel: Selected | null, override: PoseOverride | null, clip: Clip | undefined, frame: number): string | null {
  if (sel === null) return null;
  const rot = override?.nodes[sel.name]?.rot ?? (clip === undefined ? undefined : startKeyOf(clip, sel.name, frame).rot);
  return rot === undefined ? null : `yaw ${fmt(rot[0])} pitch ${fmt(rot[1])} roll ${fmt(rot[2])}`;
}

/** Clip name and frame (of the clip's length when known). */
const frameText = (name: string | null, frame: number, clip: Clip | undefined): string =>
  `${name ?? 'no clip'} ${String(frame)}${clip === undefined ? '' : `/${String(clip.frames)}`}`;

/** The pose-read yaw / pitch the facet lookup used for the joint. */
const viewText = (sel: Selected | null): string | null => (sel === null ? null : `view yaw ${fmt(sel.yawDeg)} pitch ${fmt(sel.pitchDeg)}`);

/** The running drag's readout (the value the release stores), else why the tool shows fewer handles or none. */
const dragText = (hud: ViewportHud | null): string | null => hud?.readout ?? hud?.note ?? null;

/** Reads the store (frame, clip, tool, override) and the HUD snapshot (selected joint, picks) of the instance. */
export function ViewportStatus({ hud }: { hud: ViewportHud | null }): ReactElement {
  const tool = useEditor((s) => s.tool);
  const frame = useEditor((s) => s.playhead.frame);
  const name = useEditor((s) => s.playhead.clip);
  const clip = useEditor((s) => (s.playhead.clip === null ? undefined : s.puppet.doc?.clips[s.playhead.clip]));
  const override = useEditor((s) => s.poseOverride);
  const sel = hud?.selected ?? null;
  const parts = [dragText(hud), pickText(sel?.picks[0]), rotText(sel, override, clip, frame), viewText(sel)].filter((t): t is string => t !== null);
  return (
    <div style={S.status} role="status">
      <span>{jointText(sel)}</span>
      {parts.map((t) => <span key={t} style={S.label}>{t}</span>)}
      <span>{frameText(name, frame, clip)}</span>
      <span style={S.label}>{HINTS[tool]} | Alt+drag orbit, middle pan, wheel zoom, right click menu</span>
    </div>
  );
}
